import { useFormik } from 'formik';

import { useTranslation } from 'common/hooks/useTranslation';
import { useNotifications } from 'modules/app/notifications';
import {
  VAT_NUMBER_MIN_LENGTH,
  VAT_NUMBER_MAX_LENGTH,
} from 'modules/suppliers/components/SupplierSpanishDprComplianceForm/VatNumberFormField/VatNumberFormField';

import { useCreateSupplier, useUpdateSupplier } from '../api';

type Props = {
  supplier?: {
    id: string;
    name: string;
    country?: string;
    vatNumber?: string;
  };
  onSupplierSelected: (supplier: { id: string; name: string }) => void;
  shouldDisplayDPRInformations: boolean | undefined;
  onSuccess?: () => void;
};

export const useSupplierEditForm = ({
  supplier,
  onSupplierSelected,
  shouldDisplayDPRInformations,
  onSuccess,
}: Props) => {
  const { t } = useTranslation('global');
  const { successNotif } = useNotifications();

  const [
    createSupplier,
    createSupplierQueryState,
    resetCreateSupplierQueryState,
  ] = useCreateSupplier({
    onSupplierCreationSuccess: () => {
      successNotif(t('suppliers.updateSuccess'));
    },
  });

  const [
    updateSupplier,
    updateSupplierQueryState,
    resetUpdateSupplierQueryState,
  ] = useUpdateSupplier(supplier?.id ?? '', {
    onSupplierUpdateSuccess: () => {
      successNotif(t('suppliers.updateSuccess'));
    },
  });

  const resetQueryStates = () => {
    resetCreateSupplierQueryState();
    resetUpdateSupplierQueryState();
  };

  const formikProps = useFormik<{
    newSupplierName?: string;
    vatNumber?: string;
    country?: string;
  }>({
    initialValues: {
      vatNumber: supplier?.vatNumber,
      country: supplier?.country,
      newSupplierName: undefined,
    },
    onSubmit: async (values) => {
      resetQueryStates();
      try {
        if (values.newSupplierName?.length) {
          const newSupplier = await createSupplier({
            name: values.newSupplierName,
            address: { country: values.country },
            legalInfos: {
              legalName: values.newSupplierName,
              vatNumber: values.vatNumber,
            },
            isArchived: false,
            isSupplierValidated: false,
          });
          onSupplierSelected(newSupplier);
          return;
        }

        if (supplier?.id) {
          await updateSupplier({
            id: supplier?.id,
            address: { country: values.country },
            legalInfos: {
              vatNumber: values.vatNumber,
            },
          });
        }

        onSuccess?.();
      } catch {
        // handled by the query state
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    validate: async (values) => {
      if (!supplier && !values.newSupplierName) {
        return { newSupplierName: 'required' };
      }
      if (shouldDisplayDPRInformations) {
        const errors: { country?: string; vatNumber?: string } = {};

        if (!values.country?.length) {
          errors.country = 'required';
        }

        if (!values.vatNumber?.length) {
          errors.vatNumber = 'required';
        } else if (
          values.vatNumber.length < VAT_NUMBER_MIN_LENGTH ||
          values.vatNumber.length > VAT_NUMBER_MAX_LENGTH
        ) {
          errors.vatNumber = 'invalidLength';
        }

        if (Object.keys(errors).length > 0) {
          return errors;
        }
      }
    },
  });

  return {
    formikProps: {
      ...formikProps,
      errors: formikProps.errors,
    },
    createSupplierQueryState,
    updateSupplierQueryState,
  };
};
